import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import PunchClockIcon from '@mui/icons-material/PunchClock';
import {
  hasValue,
  Sheet,
  SheetFooter,
  SheetHeader,
  SheetPaper,
  useDrawer,
} from 'swipeclock-frontend';
import { useSetSnackbarContext } from 'providers/SnackbarProvider';
import { OFFLINE_PUNCH_ACTIVE_STATUSES } from '../constants/offlinePunchQueueStatuses';
import { webClockContextKeys } from '../constants/webClockContextKeys';
import { OFFLINE_PUNCH_STATUS_LABELS } from '../constants/offlinePunchLabels';
import {
  OFFLINE_PUNCH_ERROR_BANNER_COLORS,
  OFFLINE_STATUS_TOOLBAR_LABELS,
} from '../constants/offlineStatusToolbarLabels';
import { useOfflinePunchDeleteConfirmation } from '../hooks/useOfflinePunchDeleteConfirmation';
import { useOfflinePunchQueue } from '../hooks/useOfflinePunchQueue';
import OfflinePunchDeleteConfirmAlert from './OfflinePunchDeleteConfirmAlert';
import { formatUTCTime } from '../utils/formats';
import {
  getOfflinePunchDisplayStatus,
  getOfflinePunchDisplayStatusColor,
  hasFailedOfflinePunch,
  OFFLINE_PUNCH_DISPLAY_STATUS,
} from '../utils/offlinePunchDisplayStatus';
import {
  getOfflinePunchLabel,
  groupOfflinePunchesByDate,
} from '../utils/offlinePunchDrawerUtils';

const hasActiveStatusDisplay = (items, now = Date.now()) =>
  items.some(
    (item) =>
      item.status === 'syncing' ||
      item.submitDisplayUntil > now ||
      (item.retryDisplayUntil > now && item.retryCount > 0)
  );

const {
  OFFLINE_PUNCH_SUBMIT_ERROR_TITLE,
  OFFLINE_PUNCH_SUBMIT_ERROR_SUBTITLE,
} = OFFLINE_STATUS_TOOLBAR_LABELS;

export default function OfflinePunchDrawerContent({ id }) {
  const theme = useTheme();
  const { queue, deletePunch } = useOfflinePunchQueue();
  const setSnackbar = useSetSnackbarContext();
  const { closeDrawer } = useDrawer(webClockContextKeys.OFFLINE_PUNCHES);
  const primaryColor = theme.palette.primary.main;

  const visibleQueue = useMemo(
    () =>
      queue.filter((item) => OFFLINE_PUNCH_ACTIVE_STATUSES.has(item.status)),
    [queue]
  );
  const [retryDisplayTick, setRetryDisplayTick] = useState(0);
  const shouldRefreshStatusDisplay = useMemo(
    () => hasActiveStatusDisplay(visibleQueue),
    [visibleQueue, retryDisplayTick]
  );

  useEffect(() => {
    if (!shouldRefreshStatusDisplay) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setRetryDisplayTick((tick) => tick + 1);
    }, 250);

    return () => clearInterval(intervalId);
  }, [shouldRefreshStatusDisplay, visibleQueue]);

  const groupedQueue = useMemo(
    () => groupOfflinePunchesByDate(visibleQueue),
    [visibleQueue]
  );
  const visiblePunchIds = useMemo(
    () => visibleQueue.map((item) => item.id),
    [visibleQueue]
  );

  const hasFailedPunch = useMemo(() => hasFailedOfflinePunch(queue), [queue]);

  const {
    pendingPunchId,
    isDeleting,
    requestDelete,
    cancelDelete,
    confirmDelete,
    isDeleteDisabled,
  } = useOfflinePunchDeleteConfirmation({
    deletePunch,
    setSnackbar,
    visiblePunchIds,
  });

  const handleCloseDrawer = useCallback(() => {
    cancelDelete();
    closeDrawer();
  }, [cancelDelete, closeDrawer]);

  return (
    <Sheet>
      <SheetHeader onClose={handleCloseDrawer}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: theme.spacing(2),
            backgroundColor: theme.palette.white,
            '& *': {
              userSelect: 'none',
            },
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <PunchClockIcon sx={{ color: primaryColor }} />
            <Typography variant="h6" sx={{ color: primaryColor }}>
              {`Stored Punches (${visibleQueue.length})`}
            </Typography>
          </Stack>
          <IconButton onClick={handleCloseDrawer}>
            <CloseIcon edge="end" />
          </IconButton>
        </Box>
      </SheetHeader>
      <SheetPaper>
        <Divider sx={{ mb: 2 }} />
        {visibleQueue.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No offline punches are waiting to sync.
          </Typography>
        ) : (
          <Stack spacing={2} sx={{ ml: 1 }}>
            {groupedQueue.map((group) => (
              <Box key={group.dateHeader}>
                <Typography
                  id={`${id}__date-${group.dateHeader}`}
                  sx={{ mb: 1 }}
                  variant="subtitle2"
                >
                  {group.dateHeader}
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    listStyleType: 'disc',
                    m: 0,
                    pl: 3,
                  }}
                >
                  {group.punches.map((item) => {
                    const displayStatus = getOfflinePunchDisplayStatus(item);
                    const statusColor = getOfflinePunchDisplayStatusColor(
                      displayStatus,
                      theme
                    );
                    const isFailed =
                      displayStatus === OFFLINE_PUNCH_DISPLAY_STATUS.FAILED;
                    const isConfirmingDelete = pendingPunchId === item.id;
                    const isUnselectedDuringConfirm =
                      hasValue(pendingPunchId) && !isConfirmingDelete;
                    const mutedTextColor = theme.palette.action.disabled;

                    return (
                      <Box
                        component="li"
                        id={`${id}__item-${item.id}`}
                        key={item.id}
                        sx={{ mb: 1 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                color: isUnselectedDuringConfirm
                                  ? mutedTextColor
                                  : undefined,
                              }}
                              variant="body2"
                            >
                              <Box component="span" sx={{ fontWeight: 'bold' }}>
                                {getOfflinePunchLabel(item.punch)}
                              </Box>
                              {`: ${formatUTCTime(item.punchDateTime, {
                                clockAmPm: true,
                              })}`}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isUnselectedDuringConfirm
                                  ? mutedTextColor
                                  : statusColor,
                                display: 'block',
                              }}
                            >
                              {OFFLINE_PUNCH_STATUS_LABELS[displayStatus]}
                            </Typography>
                          </Box>
                          {isFailed && (
                            <IconButton
                              aria-label="Delete failed offline punch"
                              disabled={isDeleteDisabled(item.id)}
                              id={`${id}__delete-${item.id}`}
                              onClick={() => requestDelete(item.id)}
                              size="small"
                              sx={{
                                color: theme.palette.error.main,
                                mt: -0.5,
                                '&.Mui-disabled': {
                                  color: theme.palette.action.disabled,
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                        {isFailed && (
                          <OfflinePunchDeleteConfirmAlert
                            id={`${id}__delete-confirm-${item.id}`}
                            isDeleting={isDeleting}
                            onCancel={cancelDelete}
                            onConfirm={confirmDelete}
                            open={isConfirmingDelete}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </SheetPaper>
      {hasFailedPunch && (
        <SheetFooter>
          <Alert
            icon={false}
            severity="error"
            variant="outlined"
            sx={{
              alignItems: 'center',
              backgroundColor: OFFLINE_PUNCH_ERROR_BANNER_COLORS.background,
              borderColor: OFFLINE_PUNCH_ERROR_BANNER_COLORS.main,
              color: OFFLINE_PUNCH_ERROR_BANNER_COLORS.main,
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <ErrorIcon aria-hidden sx={{ flexShrink: 0 }} />
              <Box>
                <Typography
                  component="p"
                  sx={{
                    fontWeight: 600,
                    lineHeight: 1.4,
                    m: 0,
                  }}
                >
                  {OFFLINE_PUNCH_SUBMIT_ERROR_TITLE}
                </Typography>
                <Typography
                  component="p"
                  sx={{
                    fontWeight: 400,
                    lineHeight: 1.4,
                    m: 0,
                    mt: 0.5,
                  }}
                  variant="body2"
                >
                  {OFFLINE_PUNCH_SUBMIT_ERROR_SUBTITLE}
                </Typography>
              </Box>
            </Box>
          </Alert>
        </SheetFooter>
      )}
    </Sheet>
  );
}

OfflinePunchDrawerContent.propTypes = {
  id: PropTypes.string.isRequired,
};
